import React from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import KpiTile from "./KpiTile";
import { useChartTheme, formatCurrency, formatNumber } from "./dashboardUtils";

const CHART_HEIGHT = 280;

const formatNights = (value) =>
  `${Number(value || 0).toLocaleString("en-US", {
    maximumFractionDigits: 1,
  })} nights`;

const CustomersSection = ({ customers, summary }) => {
  const chartTheme = useChartTheme();
  const { current, previous } = summary;
  const {
    top,
    most_popular_package: topPackage,
    most_popular_month: topMonth,
    check_ins_by_month: byMonth,
  } = customers;

  const customersTable = (
    <TableContainer style={{ maxHeight: 440 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell align="right">Packages</TableCell>
            <TableCell align="right">Condo stays</TableCell>
            <TableCell align="right">Nights</TableCell>
            <TableCell align="right">Avg nights</TableCell>
            <TableCell align="right">Spent</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {top.map((c, i) => (
            <TableRow key={c.customer_id} hover>
              <TableCell>{i + 1}</TableCell>
              <TableCell>
                <Link
                  component={RouterLink}
                  to={`/sales/customers/${c.customer_id}`}
                >
                  {c.name}
                </Link>
                {c.email && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="div"
                  >
                    {c.email}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                {formatNumber(c.package_orders)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(c.condo_orders)}
              </TableCell>
              <TableCell align="right">{formatNumber(c.nights)}</TableCell>
              <TableCell align="right">{c.avg_nights}</TableCell>
              <TableCell align="right">{formatCurrency(c.spent)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const monthChart = (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart
        data={byMonth}
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
      >
        <CartesianGrid vertical={false} stroke={chartTheme.grid} />
        <XAxis
          dataKey="name"
          stroke={chartTheme.grid}
          tick={{ fill: chartTheme.muted, fontSize: 12 }}
          tickLine={false}
          tickFormatter={(name) => name.slice(0, 3)}
          interval={0}
        />
        <YAxis
          stroke={chartTheme.grid}
          tick={{ fill: chartTheme.muted, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={32}
          allowDecimals={false}
        />
        <Tooltip
          cursor={{ fill: chartTheme.grid }}
          content={
            <ChartTooltip
              labelFormatter={(name, payload) => {
                const row = payload[0] && payload[0].payload;
                return row
                  ? `${name} · ${formatNights(row.nights)} · ${formatCurrency(
                      row.booked
                    )}`
                  : name;
              }}
              formatter={formatNumber}
            />
          }
        />
        <Bar
          dataKey="orders"
          name="Check-ins"
          fill={chartTheme.series[0]}
          radius={[4, 4, 0, 0]}
          maxBarSize={28}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const monthTable = (
    <TableContainer style={{ maxHeight: CHART_HEIGHT + 40 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Month</TableCell>
            <TableCell align="right">Check-ins</TableCell>
            <TableCell align="right">Nights</TableCell>
            <TableCell align="right">Booked</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {byMonth.map((m) => (
            <TableRow key={m.month}>
              <TableCell>{m.name}</TableCell>
              <TableCell align="right">{formatNumber(m.orders)}</TableCell>
              <TableCell align="right">{formatNumber(m.nights)}</TableCell>
              <TableCell align="right">{formatCurrency(m.booked)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={3}>
        <KpiTile
          label="Most popular package"
          value={topPackage ? topPackage.name : "—"}
          hint={
            topPackage
              ? `${formatNumber(topPackage.orders)} orders · ${formatCurrency(
                  topPackage.avg_order_value
                )} avg · ${formatNights(topPackage.avg_nights)} avg`
              : "No package orders in this period"
          }
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <KpiTile
          label="Most popular travel month"
          value={topMonth ? topMonth.name : "—"}
          hint={
            topMonth
              ? `${formatNumber(topMonth.orders)} check-ins · ${formatNights(
                  topMonth.nights
                )}`
              : "No stays in this period"
          }
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <KpiTile
          label="Average nights per order"
          value={formatNights(current.avg_nights_per_order)}
          current={current.avg_nights_per_order}
          previous={previous.avg_nights_per_order}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <KpiTile
          label="Average spent per package"
          value={formatCurrency(current.avg_package_order_value)}
          current={current.avg_package_order_value}
          previous={previous.avg_package_order_value}
          hint={`Across ${formatNumber(current.package_orders)} package orders`}
        />
      </Grid>
      <Grid item xs={12} lg={7}>
        <ChartCard
          title="Top customers"
          subtitle="Ranked by amount paid, net of refunds"
          isEmpty={top.length === 0}
          table={customersTable}
        />
      </Grid>
      <Grid item xs={12} lg={5}>
        <ChartCard
          title="Check-ins by month"
          subtitle="When guests travel, all years in the range combined"
          isEmpty={!topMonth}
          chart={monthChart}
          table={monthTable}
        />
      </Grid>
    </Grid>
  );
};

export default CustomersSection;
